import React from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * 공통 스피너 컴포넌트
 *
 * @param {string}  size      - 'sm' (20px) | 'md' (32px, 기본) | 'lg' (48px)
 * @param {string}  color     - 스피너 색상 (기본: #4A6741)
 * @param {string}  variant   - 'spin' (기본 회전) | 'dots' (점 3개) | 'pulse' (맥박)
 * @param {boolean} fullPage  - true면 화면 전체 중앙 배치
 * @param {string}  text      - 스피너 아래 텍스트
 * @param {boolean} overlay   - true면 반투명 오버레이 배경
 */
const Spinner = ({
  size = 'md',
  color = '#4A6741',
  variant = 'dots',
  fullPage = false,
  text,
  overlay = false,
  style
}) => {
  const content = (
    <Wrap $fullPage={fullPage} $overlay={overlay} style={style}>
      {variant === 'spin' && <SpinRing $size={SIZE[size]} $color={color} />}
      {variant === 'dots' && (
        <DotsWrap>
          {[0, 1, 2].map(i => (
            <DotItem key={i} $size={SIZE[size]} $color={color} $delay={i * 0.15} />
          ))}
        </DotsWrap>
      )}
      {variant === 'pulse' && <PulseCircle $size={SIZE[size]} $color={color} />}
      {text && <SpinnerText $color={color}>{text}</SpinnerText>}
    </Wrap>
  );

  if (overlay) return <Overlay>{content}</Overlay>;
  return content;
};

export default Spinner;

const SIZE = { sm: 20, md: 32, lg: 48 };

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

const pulseAnim = keyframes`
  0% { transform: scale(0.85); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.4; }
  100% { transform: scale(0.85); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.6);
  backdrop-filter: blur(2px);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  ${({ $fullPage }) => $fullPage && `
    width: 100%;
    min-height: 200px;
  `}
`;

const SpinRing = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: ${({ $size }) => Math.max(2, $size / 10)}px solid rgba(0,0,0,0.08);
  border-top-color: ${({ $color }) => $color};
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const DotsWrap = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const DotItem = styled.div`
  width: ${({ $size }) => $size * 0.35}px;
  height: ${({ $size }) => $size * 0.35}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  animation: ${bounce} 1s ease-in-out ${({ $delay }) => $delay}s infinite;
`;

const PulseCircle = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  animation: ${pulseAnim} 1.2s ease-in-out infinite;
`;

const SpinnerText = styled.p`
  font-size: 13px;
  color: ${({ $color }) => $color};
  font-weight: 500;
`;
